import type { FacetConfig, FacetCounts, FilterState, SearchConfig, SortState } from "../types/DataControls";
import { useClientDataControls } from "./useClientDataControls";
import { type ServerDataState, useServerDataControls } from "./useServerDataControls";

// ============================================================================
// Client Mode
// ============================================================================

export interface UseDataControlsClientOptions<T> {
  mode: "client";
  /** Raw data to process (required in client mode) */
  data: T[];
  /** Filter state */
  filters?: FilterState;
  /** Sort state */
  sort?: SortState | null;
  /** Search configuration */
  search?: SearchConfig | null;
  /** Facet configurations */
  facetConfigs?: FacetConfig[];
}

// ============================================================================
// Server Mode
// ============================================================================

export interface UseDataControlsServerOptions<T> {
  mode: "server";
  /** Data from server (already filtered/sorted) */
  data: T[];
  /** Filter state */
  filters: FilterState;
  /** Sort state */
  sort: SortState | null;
  /** Search configuration */
  search: SearchConfig | null;
  /** Callback when state changes (required in server mode) */
  onStateChange: (state: ServerDataState) => void;
  /** Search debounce delay in ms (default: 300) */
  searchDebounce?: number;
  /** Result count from server */
  resultCount?: number;
  /** Facet counts from server */
  facetCounts?: FacetCounts;
}

// ============================================================================
// Union Type
// ============================================================================

export type UseDataControlsOptions<T> = UseDataControlsClientOptions<T> | UseDataControlsServerOptions<T>;

// ============================================================================
// Result Type
// ============================================================================

export interface UseDataControlsResult<T> {
  /** Processed data (client mode) or server data (server mode) */
  data: T[];
  /** Facet counts (computed in client mode, passed through in server mode) */
  facetCounts: FacetCounts;
  /** Result count (computed in client mode, passed through in server mode) */
  resultCount: number;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useDataControls
 *
 * Unified hook for managing data filtering, sorting, searching, and faceting.
 * Supports both client-side and server-side modes.
 *
 * **Client Mode:**
 * - Processes data locally using pure functions
 * - Computes facet counts automatically
 * - Best for datasets < 5000 rows
 *
 * **Server Mode:**
 * - Notifies parent when state changes (debounced for search)
 * - Passes through server-provided data and counts
 * - Best for large datasets or complex queries
 *
 * @example Client Mode
 * ```tsx
 * const { data, facetCounts, resultCount } = useDataControls({
 *   mode: 'client',
 *   data: rawData,
 *   filters: createFilters([...]),
 *   sort: { field: 'name', direction: 'asc' },
 *   search: { query: searchQuery, fields: ['name', 'email'] },
 *   facetConfigs: [{ field: 'department', label: 'Department' }]
 * });
 * ```
 *
 * @example Server Mode
 * ```tsx
 * const { data, facetCounts, resultCount } = useDataControls({
 *   mode: 'server',
 *   data: serverData, // Already filtered
 *   filters,
 *   sort,
 *   search,
 *   resultCount: totalCount,
 *   facetCounts: serverFacets,
 *   onStateChange: (state) => refetch(state)
 * });
 * ```
 */
export function useDataControls<T>(options: UseDataControlsOptions<T>): UseDataControlsResult<T> {
  if (options.mode === "client") {
    // Client mode: process data locally
    const result = useClientDataControls<T>({
      data: options.data,
      filters: options.filters,
      sort: options.sort,
      search: options.search,
      facetConfigs: options.facetConfigs,
    });

    return result;
  }

  // Server mode: notify on state changes, pass through server data
  useServerDataControls({
    filters: options.filters,
    sort: options.sort,
    search: options.search,
    onStateChange: options.onStateChange,
    searchDebounce: options.searchDebounce,
  });

  return {
    data: options.data,
    facetCounts: options.facetCounts || {},
    resultCount: options.resultCount ?? options.data.length,
  };
}
